import { useCallback, useMemo } from 'react'

import debounce from 'lodash/debounce'

import { useRouter } from 'expo-router'

import { observer } from 'mobx-react-lite'

import { Pressable, StyleSheet, View } from 'react-native'

import { DS } from '@/design-system'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { GameModeTag } from '@/models/api-store'
import { isMaintenanceModeEnabled, showMaintenanceModeAlert } from '@/screens/play-screen/maintenanceMode'
import { ACTION_ADVISOR_COPY } from '@/screens/GameContainer/actionAdvisor.constants'

// Slim full-width banner promo for the advisor (Evie), styled as an ad strip
// (not a nav row): solid Brand/Blue pill, avatar at the left edge, two-line
// copy, NEW badge and chevron. Links straight to create-a-solo-game (the
// create-game screen opens on the Solo tab, and the advisor is live in solo
// practice games). Roughly half the height of the Play Poker nav rows.
export const AdvisorPromoBanner = observer(() => {
  const handlePress = useAdvisorPromo()

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={PROMO_ACCESSIBILITY_LABEL}
      style={styles.banner}
    >
      {/* Circular crop wrapper (same defense as the in-game avatar): the
          avatar SVG already crops itself with an internal clipPath, and the
          transformer namespaces clip ids per file (metro.transformer.js), so
          the ids no longer collide across SVGs on web's shared DOM. The
          wrapper stays as belt-and-suspenders: it crops identically on every
          platform even if the SVG's own clip is ever edited away. */}
      <View style={styles.badge}>
        <DS.Text variant="Caption/Bold" color="Global/White" allowFontScaling={false}>
          NEW
        </DS.Text>
      </View>
      <View style={styles.avatarCrop}>
        <DS.Icon name="Advisor-avatar" size={ADVISOR_PROMO_AVATAR_SIZE} />
      </View>
      <View style={styles.copy}>
        <DS.Text variant="Body Small/Medium" color="Global/White" numberOfLines={1}>
          {PROMO_TITLE}
        </DS.Text>
        <DS.Text variant="Caption/Regular" color="Brand/Cream" numberOfLines={1}>
          {PROMO_SECONDARY}
        </DS.Text>
      </View>
      {/* <DS.Icon name="Arrow--right-wide" size={[12, 6]} color="Global/White" style={styles.chevron} /> */}
    </Pressable>
  )
})

const ADVISOR_PROMO_AVATAR_SIZE = 36

// Promo copy: the advisor's name comes from the shared advisor copy bank
// (ADVISOR_UI_COPY.avatarLabel, re-exported as ACTION_ADVISOR_COPY) so the
// banner, the drawer, and the in-game avatar can never disagree on the name.
const PROMO_TITLE = `Meet ${ACTION_ADVISOR_COPY.avatarLabel}, your Poker Advisor`
const PROMO_SECONDARY = 'Now in solo practice games.'
const PROMO_ACCESSIBILITY_LABEL = `${PROMO_TITLE}. ${PROMO_SECONDARY}`

// Mirrors the Practice & Play row's handler (maintenance gate, walkthrough and
// table-name resets) but logs its own promo event for attribution.
function useAdvisorPromo() {
  const router = useRouter()
  const {
    analytics: { setEvent },
    game: { setGameKind, setTableName, clearWalkthrough },
    session: { user }
  } = useStore()

  const onPress = useCallback(() => {
    if (isMaintenanceModeEnabled()) {
      showMaintenanceModeAlert()
      return
    }
    clearWalkthrough()
    setTableName('')
    setGameKind(GameModeTag.default)
    setEvent(events.playscreen.advisor_promo_tapped, {
      userId: user?._id.toString()
    })
    router.navigate('/game/new')
  }, [user, setEvent, setGameKind, setTableName, clearWalkthrough, router.navigate])

  return useMemo(() => debounce(onPress, 250), [onPress])
}

const styles = StyleSheet.create({
  // Ad-strip look, deliberately unlike the white nav row cards: solid
  // Brand/Blue pill at half a nav row's height, content edge to edge.
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DS.spacing[12],
    height: 44,
    paddingHorizontal: DS.spacing[8],
    borderRadius: 22,
    backgroundColor: DS.palette['Brand/Green']
  },
  // White circular badge 4pt larger than the avatar: the ring is the wrapper's
  // own white background showing through the 2pt padding, so the artwork can
  // never paint over it (a borderWidth ring sits under the icon, which
  // overflows the shrunken content box and covers it).
  avatarCrop: {
    width: ADVISOR_PROMO_AVATAR_SIZE + 2,
    height: ADVISOR_PROMO_AVATAR_SIZE + 2,
    borderRadius: (ADVISOR_PROMO_AVATAR_SIZE + 2) / 2,
    backgroundColor: DS.palette['Global/White'],
    padding: 1,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center'
  },
  copy: {
    flex: 1,
    gap: 0
  },
  badge: {
    backgroundColor: DS.palette['Brand/Blue'],
    paddingHorizontal: DS.spacing[6],
    paddingVertical: 2,
    borderRadius: 8,
    marginHorizontal: DS.spacing[4]
  },
  // Keeps the 24x24 icon slot from the design around the 6x12 glyph
  chevron: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center'
  }
})
